import React, { useEffect } from 'react';
import classnames from 'classnames/bind';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Layout, Button, theme } from 'antd';
import { useLocation } from 'react-router-dom';
import styles from './AppLayout.module.scss';
import SiderBar from "./SiderBar/SiderBar";
import TopMenu from "./TopMenu/TopMenu";
import SiderBarProvider from "../../app/providers/SiderBarProvider";
import { useDispatch, useSelector } from "react-redux";
import {
    getSavedCollapsedState,
    setCollapsed,
    toggleCollapsed,
} from "../../store/slices/CollapsedSlice";
import useResponsive, { SIDER_WIDTH } from "../../shared/hooks/useResponsive";

const { Header, Content } = Layout;
const cx = classnames.bind(styles);

const AppLayout = ({ children }) => {
    const location = useLocation();
    const collapsed = useSelector(state => state.collapsed.state);
    const dispatch = useDispatch();
    const { isMobile, isReady, siderCollapsedWidth } = useResponsive();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    useEffect(() => {
        if (!isReady) return;

        if (isMobile) {
            dispatch(setCollapsed({ collapsed: true, persist: false }));
        } else {
            dispatch(setCollapsed({
                collapsed: getSavedCollapsedState(),
                persist: false,
            }));
        }
    }, [isMobile, isReady, dispatch]);

    useEffect(() => {
        if (!isReady || !isMobile) return;
        dispatch(setCollapsed({ collapsed: true, persist: false }));
    }, [location.pathname, isMobile, isReady, dispatch]);

    const contentMarginLeft = isMobile
        ? 0
        : (collapsed ? siderCollapsedWidth : SIDER_WIDTH);

    const handleToggleSidebar = () => {
        if (isMobile) {
            dispatch(setCollapsed({ collapsed: !collapsed, persist: false }));
            return;
        }
        dispatch(toggleCollapsed());
    };

    const handleCloseSidebar = () => {
        if (isMobile && !collapsed) {
            dispatch(setCollapsed({ collapsed: true, persist: false }));
        }
    };

    return (
        <SiderBarProvider>
            <Layout
                className={cx('layout-root')}
                hasSider={true}
            >
                {isMobile && !collapsed && (
                    <div
                        className={cx('sidebar-overlay')}
                        onClick={handleCloseSidebar}
                        aria-hidden="true"
                    />
                )}
                <SiderBar
                    collapsed={collapsed}
                    isMobile={isMobile}
                    onMenuNavigate={handleCloseSidebar}
                />
                <Layout
                    className={cx('main-layout')}
                    style={{ marginLeft: contentMarginLeft }}
                >
                    <Header
                        className={cx('header')}
                        style={{ background: colorBgContainer }}
                    >
                        <div className={cx('nav-wrapper')}>
                            <Button
                                className={cx('btn')}
                                type="text"
                                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                onClick={handleToggleSidebar}
                            />
                            <TopMenu />
                        </div>
                    </Header>

                    <Content
                        className={cx('content')}
                        style={{ borderRadius: borderRadiusLG }}
                    >
                        {children}
                    </Content>
                </Layout>
            </Layout>
        </SiderBarProvider>
    );
};

export default AppLayout;
